/**
 * Resolvers for statement figures that may live either as first-class DTO
 * fields (snake_case) or inside `extended_fields`, where the backend parks
 * country-specific / PDF-only extras under their Neo4j PascalCase property
 * names (e.g. "Capital_Work_In_Progress"). Callers pass every spelling a value
 * can arrive under; the first non-null hit wins — so a row renders whichever
 * shape the API served, and simply stays hidden when the value is absent.
 */
import type { UnifiedFinancialRecord } from '../../../types/financial';

export function resolveRecordValue(
  record: UnifiedFinancialRecord,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const direct = (record as unknown as Record<string, unknown>)[key];
    if (typeof direct === 'number' && !Number.isNaN(direct)) return direct;

    const ext = record.extended_fields?.[key];
    if (typeof ext === 'number' && !Number.isNaN(ext)) return ext;
    if (typeof ext === 'string' && ext.trim() !== '' && !Number.isNaN(Number(ext))) {
      return Number(ext);
    }
  }
  return null;
}

/**
 * Assumed_Inputs travels on the CalculatedMetrics node as a string[]. It can
 * reach the client as a real array (first-class field) or — because
 * extended_fields only carries scalars — as a JSON-encoded string. Normalise
 * both; the strings themselves are rendered verbatim, never re-worded.
 */
export function resolveAssumedInputs(record: UnifiedFinancialRecord): string[] {
  const raw: unknown =
    record.assumed_inputs ??
    (record as unknown as Record<string, unknown>)['Assumed_Inputs'] ??
    record.extended_fields?.['Assumed_Inputs'] ??
    record.extended_fields?.['assumed_inputs'];

  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map(String).filter((s) => s.trim() !== '');
  }
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!text) return [];
    if (text.startsWith('[')) {
      try {
        const parsed: unknown = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parsed.map(String).filter((s) => s.trim() !== '');
        }
      } catch {
        // not JSON — fall through and treat as a single verbatim entry
      }
    }
    return [text];
  }
  return [];
}

/** Calc_Engine, e.g. "india_calculator_service" / "us_calculator_service". */
export function resolveCalcEngine(record: UnifiedFinancialRecord): string | null {
  const raw: unknown =
    record.calc_engine ??
    (record as unknown as Record<string, unknown>)['Calc_Engine'] ??
    record.extended_fields?.['Calc_Engine'] ??
    record.extended_fields?.['calc_engine'];
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}

/**
 * How WACC was derived — India CM stores WACC_Method ("book_value_assumed"),
 * US stores wacc_calculation_method ("book_value_weights_no_market_cap").
 * Surfaced as a provenance line under the Assumptions panel.
 */
export function resolveWaccMethod(record: UnifiedFinancialRecord): string | null {
  const raw: unknown =
    (record as unknown as Record<string, unknown>)['wacc_calculation_method'] ??
    (record as unknown as Record<string, unknown>)['WACC_Method'] ??
    record.extended_fields?.['WACC_Method'] ??
    record.extended_fields?.['wacc_calculation_method'];
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}
